<script setup lang="ts">
import {type PortfolioType, type ReviewType} from "~/types/Portfolio";
import {endpoints} from "~/constants/endpoints";
import MasterPage from '~/components/pages/master/MasterPage.vue'
import MasterPageGeo from '~/components/pages/master/MasterPageGeo.vue'

definePageMeta({
    name: 'portfolio'
})

const globalStore = useGlobalStore()
const config = useRuntimeConfig()

const PortfolioMode = {
    DEFAULT: 'default',
    GEO: 'geo',
} as const

const route = useRoute();
const mode = ref(PortfolioMode.DEFAULT)
const similarSpecialists = ref<PortfolioType[]>([])

watch(() => route.query?.mode, () => {
    if(route.query?.mode === PortfolioMode.GEO){
        mode.value = PortfolioMode.GEO
    } else {
        mode.value = PortfolioMode.DEFAULT
    }
})

const {data: portfolio } = await useAsyncData(`portfolio-${route.params.id}`,
    () => fetchData<PortfolioType[]>(endpoints.portfolio.getById(<string>route.params.id), {
        method: 'GET',
    }).then(({data, meta}) => data || [])
)

const parentSpecification = computed(() => globalStore.specifications.find(item => item.documentId === portfolio.value?.specifications[0].documentId));
const EducationTypeList = []
const reviewsPage = ref(1)
const reviewsPerPage = ref(6)
const reviews = ref<ReviewType[]>([])

async function getSimilarSpecialists() {
    if (!parentSpecification.value?.documentId) return []
    
    return await fetchData<PortfolioType[]>(endpoints.portfolio.search, {
        method: 'GET',
        query: {
            specificationId: parentSpecification.value.documentId,
            page: 1,
            pageSize: 10,
        }
    }).then(({data, meta}) => {
        return data?.filter(item => item.documentId !== portfolio.value?.documentId) || [];
    }).catch(e => {
        console.error(e)
    })
}

// Добавляем watch для отслеживания изменений portfolio
watch(() => portfolio.value, async (newPortfolio) => {
    if (newPortfolio && parentSpecification.value?.documentId) {
        similarSpecialists.value = await getSimilarSpecialists()
    }
}, { immediate: true })

// Убираем вызов из onMounted, так как теперь это обрабатывается через watch
onMounted(() => {
    if(route.query?.mode === PortfolioMode.GEO){
        mode.value = PortfolioMode.GEO
    } else {
        mode.value = PortfolioMode.DEFAULT
    }
})

const addReviews = (data: ReviewType[]) => {
    reviews.value = [...reviews.value, ...data]
}

const {data, refresh: refreshReviews } = await useAsyncData(`reviews-portfolio-${route.params.id}`,
    () => fetchData<ReviewType[]>(endpoints.portfolio.reviews, {
        method: 'GET',
        query: {
            'filters[user][documentId][$eq]': route.params.id,
            'populate[task][populate][executor]': true,
            'filters[task][executor][documentId][$eq]': route.params.id,
            pagination: {
                page: reviewsPage.value,
                pageSize: reviewsPerPage.value
            }
        }
    })
    .then((res) => addReviews(res?.data || []))
    .catch((e) => {
        console.error(e)
    })
)

watch(reviewsPage, () => {
    refreshReviews()
})

const review_tags = computed(() => {
    return [...new Set(
        portfolio.value?.user?.reviews
            .flatMap(item => item?.review_tags || [])
            .map(tag => tag.name)
    )]
})

function loadMoreReviews() {
    reviewsPage.value++
}

useSeoMeta({
    robots: 'index, follow',
    title: portfolio.value?.user?.username,
    description: () => portfolio.value?.description,
    ogImage: portfolio.value?.user?.avatar,
    ogImageType: 'image/jpeg',
    ogType: 'website',
    ogUrl: () => config.public.frontUrl + route.path,
    ogTitle: () => portfolio.value?.user?.username,
    ogDescription: () => portfolio.value?.description,
    twitterTitle: () => portfolio.value?.user?.username,
    twitterDescription: () => portfolio.value?.description,
    twitterImage: portfolio.value?.user?.avatar,
    twitterImageType: 'image/jpeg',
})

useHead({
    link: [
        {
            rel: 'canonical',
            href: config.public.frontUrl + route.path,
        },
    ],
})

const { jsonLd } = useJsonLdSchema({
    breadcrumbs: computed(() => {
        const items = [];

        if (parentSpecification.value) {
            items.push({
                name: parentSpecification.value.name,
                url: `${config.public.frontUrl}/category/${parentSpecification.value.slug}`
            });
        }

        if (portfolio.value) {
            items.push({
                name: portfolio.value?.user?.username,
                url: `${config.public.frontUrl}${route.path}`
            });
        }

        return items;
    }),
    pageData: computed(() => ({
        name: portfolio.value?.user?.username,
        description: portfolio.value?.description,
        url: `${config.public.frontUrl}${route.path}`
    })),
    homeUrl: config.public.frontUrl
});

useHead({
  script: [
    {
      type: 'application/ld+json',
      id: 'custom-jsonld-schema',
      children: JSON.stringify(jsonLd.value)
    }
  ]
})

</script>

<template>
    <div class="container">
        <MasterPageGeo
            v-if="mode === PortfolioMode.GEO"
            @changeMode="(value) => mode = value"
            @loadMoreReviews="loadMoreReviews"
            :portfolio="portfolio"
            :educationTypeList="EducationTypeList"
            :specification_name="parentSpecification?.name"
            :review_tags="review_tags"
            :reviews="reviews?.data || []"
            :reviews_total="reviews?.meta?.total || 0"
            :similarSpecialists="similarSpecialists"
        />
        <MasterPage
            v-else
            @changeMode="(value) => mode = value"
            @loadMoreReviews="loadMoreReviews"
            :portfolio="portfolio"
            :educationTypeList="EducationTypeList"
            :specification_name="parentSpecification?.name"
            :review_tags="review_tags"
            :reviews="reviews?.data || []"
            :reviews_total="reviews?.meta?.total || 0"
            :similarSpecialists="similarSpecialists"
        />

    </div>
</template>